import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import AdminDashboardNav from '../comps/AdminDashboardNav';
import { 
  FiShoppingCart, 
  FiDollarSign, 
  FiTrendingUp,
  FiCalendar,
  FiSearch,
  FiRefreshCw,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiHash,
} from 'react-icons/fi';
import '../styles/admin.css';


export default function AdminDashboard() {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('date-desc');
  const [stats, setStats] = useState({
    totalUsers: 0,
    totalOrders: 0,
    totalRevenue: 0,
    capturedRevenue: 0,
    refundedRevenue: 0,
    pendingRevenue: 0,
    todayOrders: 0,
    todayRevenue: 0,
    capturedOrders: 0,
    refundedOrders: 0,
    pendingOrders: 0
  });

  // Refund state comes from Firestore (each order's own refundedItems/fullyRefunded
  // fields, written by the backend's /refund-payment route), not localStorage - a
  // per-browser cache here would silently show wrong revenue/status on any other
  // browser or device, exactly the bug CustomerOrderList.jsx was already fixed for.
  const [refundedItems, setRefundedItems] = useState({});
  const [refundedOrders, setRefundedOrders] = useState({});

  // Helper function to determine order status (same as CustomerOrderList).
  // Takes the refund maps as params (defaulting to current state) so fetchData can
  // pass freshly-fetched maps in directly instead of racing the setState commit.
  const getOrderStatus = (order, refundedItemsMap = refundedItems, refundedOrdersMap = refundedOrders) => {
    const isFullyRefunded = refundedOrdersMap[order.orderReference];
    const isCaptured = order.captureStatus === 'CAPTURED';
    // Check for partial refunds
    const orderItems = order.items || order.products || [];
    let hasPartialRefunds = false;
    let totalRefundedAmount = 0;
    orderItems.forEach(item => {
      const key = `${order.orderReference}_${item.name}`;
      const refundedQty = refundedItemsMap[key] || 0;
      if (refundedQty > 0 && refundedQty < (item.quantity || 1)) {
        hasPartialRefunds = true;
      }
      totalRefundedAmount += refundedQty * (item.price || 0);
    });
    if (isFullyRefunded) {
      return 'fully-refunded';
    }
    if (hasPartialRefunds || totalRefundedAmount > 0) {
      return 'partially-refunded';
    }
    if (isCaptured) {
      return 'captured-paid';
    }
    return 'pending-authorized';
  };

  // Helper function to get status display info
  const getStatusInfo = (status) => {
    switch (status) {
      case 'captured-paid':
        return { label: 'Betalt', emoji: '✅' };
      case 'fully-refunded':
        return { label: 'Refundert', emoji: '💰' };
      case 'partially-refunded':
        return { label: 'Delvis refundert', emoji: '⚠️' };
      case 'pending-authorized':
      default:
        return { label: 'Kansellert', emoji: '❌' };
    }
  };

  // Helper function to calculate refund amounts for an order
  const getRefundInfo = (order, refundedItemsMap = refundedItems) => {
    const orderItems = order.items || order.products || [];
    let totalRefundedAmount = 0;
    orderItems.forEach(item => {
      const key = `${order.orderReference}_${item.name}`;
      const refundedQty = refundedItemsMap[key] || 0;
      totalRefundedAmount += refundedQty * (item.price || 0);
    });
    return { totalRefundedAmount };
  };

  // A full refund only sets fully_refunded=true and never fills in refunded_items
  // (see backend's /refund-payment), so getRefundInfo alone reads 0 refunded for a
  // fully-refunded order - callers must check status first, not just subtract refundInfo.
  const getNetOrderAmount = (order, status, refundInfo) => {
    if (status === 'fully-refunded') return 0;
    return Math.max(0, (order.totalPrice || 0) - refundInfo.totalRefundedAmount);
  };

  // Fetch all data
  const fetchData = async () => {
    setLoading(true);
    try {
      const [{ data: userRows, error: usersError }, { data: orderRows, error: ordersError }] = await Promise.all([
        supabase.from('users').select('id'),
        supabase.from('orders')
          .select('*, users(name, email), order_items(*)')
          .order('date_purchased', { ascending: false }),
      ]);
      if (usersError) throw usersError;
      if (ordersError) throw ordersError;

      const usersData = userRows || [];
      const allOrders = (orderRows || [])
        .filter(o => o.order_reference)
        .map(o => ({
          id: o.id,
          userId: o.user_id,
          userName: o.users?.name || 'Ukjent gjest',
          userEmail: o.users?.email || 'Ingen e-post oppgitt',
          orderReference: o.order_reference,
          buyerName: o.buyer_name,
          phoneNumber: o.phone_number,
          paymentMethod: o.payment_method,
          datePurchased: o.date_purchased,
          totalPrice: o.total_price,
          captureStatus: o.capture_status,
          fullyRefunded: o.fully_refunded,
          refundedItems: o.refunded_items,
          items: (o.order_items || []).map(i => ({
            name: i.name, price: i.price, quantity: i.quantity, category: i.category,
          })),
        }));
      setOrders(allOrders);

      // Derive refund state from each order's own Firestore fields (same source
      // CustomerOrderList.jsx uses) instead of the stale per-browser localStorage
      // this used to read.
      const refundedItemsFromDB = {};
      const refundedOrdersFromDB = {};
      allOrders.forEach(order => {
        if (order.refundedItems) {
          Object.assign(refundedItemsFromDB, order.refundedItems);
        }
        if (order.fullyRefunded) {
          refundedOrdersFromDB[order.orderReference] = true;
        }
      });
      setRefundedItems(refundedItemsFromDB);
      setRefundedOrders(refundedOrdersFromDB);

      // Calculate enhanced stats with revenue breakdown
      const today = new Date().toDateString();
      const todayOrders = allOrders.filter(order => {
        const d = order.datePurchased ? new Date(order.datePurchased) : null;
        return d && d.toDateString() === today;
      });
      
      const todayRevenue = todayOrders.reduce((sum, order) => {
        const status = getOrderStatus(order, refundedItemsFromDB, refundedOrdersFromDB);
        if (status === 'pending-authorized') return sum;
        const refundInfo = getRefundInfo(order, refundedItemsFromDB);
        return sum + getNetOrderAmount(order, status, refundInfo);
      }, 0);

      const totalRevenue = allOrders.reduce((sum, order) => {
        const status = getOrderStatus(order, refundedItemsFromDB, refundedOrdersFromDB);
        if (status === 'pending-authorized') return sum;
        const refundInfo = getRefundInfo(order, refundedItemsFromDB);
        return sum + getNetOrderAmount(order, status, refundInfo);
      }, 0);
      // Count orders by status and calculate revenue breakdown
      let capturedCount = 0;
      let refundedCount = 0;
      let pendingCount = 0;
      let partiallyRefundedCount = 0;
      let capturedRevenue = 0;
      let refundedRevenue = 0;
      let pendingRevenue = 0;
      allOrders.forEach(order => {
        const status = getOrderStatus(order, refundedItemsFromDB, refundedOrdersFromDB);
        const refundInfo = getRefundInfo(order, refundedItemsFromDB);
        const orderTotal = order.totalPrice || 0;
        switch (status) {
          case 'captured-paid':
            capturedCount++;
            capturedRevenue += orderTotal - refundInfo.totalRefundedAmount;
            refundedRevenue += refundInfo.totalRefundedAmount;
            break;
          case 'fully-refunded':
            refundedCount++;
            refundedRevenue += orderTotal;
            break;
          case 'partially-refunded':
            partiallyRefundedCount++;
            capturedRevenue += orderTotal - refundInfo.totalRefundedAmount;
            refundedRevenue += refundInfo.totalRefundedAmount;
            break;
          case 'pending-authorized':
          default:
            pendingCount++;
            pendingRevenue += orderTotal;
            break;
        }
      });
      setStats({
        totalUsers: usersData.length,
        totalOrders: allOrders.length,
        totalRevenue: totalRevenue,
        capturedRevenue: capturedRevenue,
        refundedRevenue: refundedRevenue,
        pendingRevenue: pendingRevenue,
        todayOrders: todayOrders.length,
        todayRevenue: todayRevenue,
        capturedOrders: capturedCount,
        refundedOrders: refundedCount,
        pendingOrders: pendingCount + partiallyRefundedCount
      });
    } catch (err) {
      setError('Klarte ikke å hente data');
      console.error(err);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter orders based on search and date
  const filteredOrders = orders.filter(order => {
    const matchesSearch =
      order.userName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.userEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.orderReference.toLowerCase().includes(searchTerm.toLowerCase()) ||
      order.buyerName?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  }).sort((a, b) => {
    switch (sortBy) {
      case 'date-desc':
        return new Date(b.datePurchased || 0) - new Date(a.datePurchased || 0);
      case 'date-asc':
        return new Date(a.datePurchased || 0) - new Date(b.datePurchased || 0);
      case 'name-asc':
        return (a.buyerName || a.userName || '').localeCompare(b.buyerName || b.userName || '');
      case 'name-desc':
        return (b.buyerName || b.userName || '').localeCompare(a.buyerName || a.userName || '');
      case 'amount-desc':
        return (b.totalPrice || 0) - (a.totalPrice || 0);
      case 'amount-asc':
        return (a.totalPrice || 0) - (b.totalPrice || 0);
      case 'status':
        return getOrderStatus(a).localeCompare(getOrderStatus(b));
      default:
        return 0;
    }
  });

  if (loading) {
    return (
      <div className="admin-loading">
        <FiRefreshCw className="loading-spinner" />
        <p>Laster dashbord...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <p>Feil: {error}</p>
        <button onClick={fetchData}>Prøv igjen</button>
      </div>
    );
  }

  return (
    <>
      <AdminDashboardNav />
      <div className="admin-dashboard">
        {/* Header */}
        <div className="admin-header">
          <div>
            <h1><FiTrendingUp style={{ marginRight: '12px', verticalAlign: 'middle' }} />Oversikt</h1>
            <p>Oversikt over systemytelse og statistikk</p>
          </div>
          <div className="admin-actions">
            <button className="refresh-btn" onClick={fetchData}>
              <FiRefreshCw />
              Oppdater
            </button>
          </div>
        </div>

        {/* Stats Overview */}
        <div className="stats-grid">
          <div className="stat-card accent">
            <div className="stat-card-header">
              <div className="stat-icon-circle">
                <FiTrendingUp />
              </div>
              <span className="stat-label">Dagens omsetning</span>
            </div>
            <div className="stat-value">{stats.todayRevenue.toFixed(0)} NOK</div>
            <div className="stat-footer">
              <span className="stat-info">
                <FiShoppingCart style={{ fontSize: '12px' }} />
                {stats.todayOrders} {stats.todayOrders === 1 ? 'bestilling' : 'bestillinger'}
              </span>
            </div>
          </div>

          <div className="stat-card secondary">
            <div className="stat-card-header">
              <div className="stat-icon-circle">
                <FiShoppingCart />
              </div>
              <span className="stat-label">Totale bestillinger</span>
            </div>
            <div className="stat-value">{stats.totalOrders}</div>
            <div className="stat-footer">
              <span className="stat-change positive">
                <FiCheckCircle style={{ fontSize: '12px' }} />
                {stats.capturedOrders} Innkrevd
              </span>
            </div>
          </div>

          <div className="stat-card warning">
            <div className="stat-card-header">
              <div className="stat-icon-circle">
                <FiClock />
              </div>
              <span className="stat-label">Ventende bestillinger</span>
            </div>
            <div className="stat-value">{stats.pendingOrders}</div>
            <div className="stat-footer">
              <span className="stat-info">
                <FiDollarSign style={{ fontSize: '12px' }} />
                {stats.pendingRevenue.toFixed(0)} NOK Ventende
              </span>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-card-header">
              <div className="stat-icon-circle">
                <FiDollarSign />
              </div>
              <span className="stat-label">Snitt per ordre</span>
            </div>
            <div className="stat-value">
              {stats.capturedOrders > 0 ? Math.round(stats.capturedRevenue / stats.capturedOrders).toLocaleString('no-NO') : 0} NOK
            </div>
            <div className="stat-footer">
              <span className="stat-info">
                <FiCheckCircle style={{ fontSize: '12px' }} />
                Basert på {stats.capturedOrders} innkrevd
              </span>
            </div>
          </div>
        </div>

        {/* Orders Table with Enhanced Info */}
        <div className="card orders-card" style={{ marginTop: '1.5rem' }}>
          <div className="orders-header">
            <h2><FiShoppingCart style={{ marginRight: '8px', verticalAlign: 'middle' }} />Siste bestillinger</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
              <span className="orders-count">{filteredOrders.length} bestillinger</span>
              <button className="refresh-btn" onClick={() => navigate('/all-orders')}>
                Se alle bestillinger
              </button>
            </div>
          </div>
          <div className="orders-filter-card">
            <div className="orders-toolbar">
              <div className="search-box">
                <FiSearch />
                <input
                  type="text"
                  placeholder="Søk bestillinger, navn, e-post..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              <select
                className="orders-sort-select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option value="date-desc">Nyeste først</option>
                <option value="date-asc">Eldste først</option>
                <option value="name-asc">Navn A–Å</option>
                <option value="name-desc">Navn Å–A</option>
                <option value="amount-desc">Beløp høy–lav</option>
                <option value="amount-asc">Beløp lav–høy</option>
                <option value="status">Status</option>
              </select>
            </div>
          </div>
          
          <div className="table-wrapper">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Bestilling</th>
                  <th>Kunde</th>
                  <th>Beløp</th>
                  <th>Status</th>
                  <th>Dato</th>
                  <th>Handlinger</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan="6" className="empty-state">
                      <p>Ingen bestillinger funnet</p>
                    </td>
                  </tr>
                ) : (
                  filteredOrders.slice(0, 20).map(order => {
                    const status = getOrderStatus(order);
                    const statusInfo = getStatusInfo(status);
                    const refundInfo = getRefundInfo(order);
                    const netAmount = getNetOrderAmount(order, status, refundInfo);
                    return (
                      <tr key={order.id} className="order-row">
                        <td>
                          <div className="order-id-cell">
                            <FiHash size={12} />
                            <span>{order.orderReference?.substring(0, 8)}</span>
                          </div>
                        </td>
                        <td>
                          <div className="customer-info">
                            <div className="customer-name">{order.buyerName || order.userName}</div>
                            <div className="customer-email">{order.userEmail}</div>
                            {order.phoneNumber && (
                              <div className="customer-phone">{order.phoneNumber}</div>
                            )}
                            <div className="customer-payment-method">
                              {order.paymentMethod === 'CARD' ? 'Kort' : 'Vipps'}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className="amount-cell">{netAmount.toFixed(0)} NOK</span>
                        </td>
                        <td>
                          <div className="status-cell">
                            <span className={`status-badge status-${status}`}>
                              {status === 'captured-paid' && <FiCheckCircle style={{ fontSize: '14px' }} />}
                              {status === 'fully-refunded' && <FiXCircle style={{ fontSize: '14px' }} />}
                              {status === 'partially-refunded' && <FiXCircle style={{ fontSize: '14px' }} />}
                              {status === 'pending-authorized' && <FiClock style={{ fontSize: '14px' }} />}
                              {statusInfo.label}
                            </span>
                            <span className="capture-status-text">{order.captureStatus || 'PENDING'}</span>
                          </div>
                        </td>
                        <td>
                          <div className="date-cell">
                            <div className="date-main">
                              <FiCalendar style={{ fontSize: '14px', marginRight: '4px' }} />
                              {order.datePurchased ? new Date(order.datePurchased).toLocaleDateString('no-NO', {
                                day: '2-digit',
                                month: 'short',
                                year: 'numeric'
                              }) : 'N/A'}
                            </div>
                            <div className="date-time">
                              {order.datePurchased ? new Date(order.datePurchased).toLocaleTimeString('no-NO', {
                                hour: '2-digit',
                                minute: '2-digit'
                              }) : ''}
                            </div>
                          </div>
                        </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-secondary view-order-btn" 
                            title="Se kundebestillinger"
                            onClick={() => window.open(`/customer-orders/${order.userId}`, '_blank')}
                          >
                            <FiShoppingCart style={{ fontSize: '16px' }} />
                            Se
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
          {filteredOrders.length > 20 && (
            <div className="load-more-row">
              <span className="load-more-info">Viser 20 av {filteredOrders.length} bestillinger</span>
              <button className="load-more-btn" onClick={() => navigate('/all-orders')}>
                Se alle bestillinger
              </button>
            </div>
          )}
        </div>      </div>
    </>
  );
}